import React, { useEffect, useMemo, useState } from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { Close, Delete, Edit } from '@mui/icons-material';
import dayjs from 'dayjs';
import withComponentAuth from '../../hoc/withComponentAuth';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import { ListTable } from '../../components/molecules/ListTable';
import { DialogBoxComp } from '../../components/atoms/DialogBox';
import useSnackbar from '../../hooks/useSnackbar';
import AddHolidayForm from '../../components/ui/add/holiday';
import {
  DELETEHoliday,
  GETHolidays,
  HOLIDAY_TYPE_LABELS,
  IHoliday,
} from '../../services/holiday';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const HEADER_CELLS: [string, number][] = [
  ['Date', 140],
  ['Day', 110],
  ['Type', 150],
  ['Description', 260],
  ['Action', 100],
];

const HolidaysManagement = () => {
  const [holidays, setHolidays] = useState<IHoliday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [searchQuery, setSearchQuery] = useState('');
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [editTarget, setEditTarget] = useState<IHoliday | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IHoliday | null>(null);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const fetchHolidays = async () => {
    try {
      setIsLoading(true);
      const list = await GETHolidays();
      setHolidays(list);
    } catch (err) {
      console.error('Failed to fetch holidays:', err);
      setHolidays([]);
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to fetch holidays',
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return holidays;
    return holidays.filter(
      (holiday) =>
        holiday.description?.toLowerCase().includes(query) ||
        HOLIDAY_TYPE_LABELS[holiday.type]?.toLowerCase().includes(query) ||
        dayjs(holiday.date).format('DD MMM YYYY').toLowerCase().includes(query),
    );
  }, [holidays, searchQuery]);

  const pageRows = useMemo(
    () => filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await DELETEHoliday(deleteTarget._id);
      showSnackbar('Holiday deleted', 'success');
      fetchHolidays();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to delete holiday',
        'error',
      );
    } finally {
      setDeleteTarget(null);
    }
  };

  const renderCell = (row: IHoliday, columnIndex: number): React.ReactNode => {
    switch (columnIndex) {
      case 0:
        return dayjs(row.date).format('DD MMM YYYY');
      case 1:
        return dayjs(row.date).format('dddd');
      case 2:
        return HOLIDAY_TYPE_LABELS[row.type] || row.type;
      case 3:
        return row.description || '—';
      default:
        return null;
    }
  };

  const isPast = (row: IHoliday) => dayjs(row.date).isBefore(dayjs(), 'day');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 1,
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}

      <Box marginBottom={'10px'}>
        <SmartFilter
          title="Holidays"
          createFilter={['SEARCH', 'ADD']}
          addbuttontitle="Add Holiday"
          addHeader={'Add Holiday'}
          addElement={<AddHolidayForm />}
          onSaved={fetchHolidays}
          onSearch={(value: string) => {
            setSearchQuery(value);
            setPage(1);
          }}
          addDrawerSize="md"
        />
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          mt: 2,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {!isLoading && filtered.length === 0 ? (
          <Typography textAlign={'center'} marginTop={'20%'} color="textSecondary">
            No Holidays Found
          </Typography>
        ) : (
          <ListTable<IHoliday>
            headerCells={HEADER_CELLS}
            rows={pageRows}
            isLoading={isLoading}
            renderCell={renderCell}
            actions={[
              {
                icon: <Edit sx={{ fontSize: 18, color: '#9ca3af' }} />,
                onClick: (row) => setEditTarget(row),
                condition: (row) => !isPast(row),
              },
              {
                icon: <Delete sx={{ fontSize: 18, color: '#EF4444' }} />,
                onClick: (row) => setDeleteTarget(row),
                condition: (row) => !isPast(row),
              },
            ]}
          />
        )}
      </Box>

      <Box display={'block'} bottom={0} right={0} mt={2}>
        <Pagination
          count={filtered.length}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={(_event: unknown, newPage: number) => setPage(newPage + 1)}
          onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(parseInt(event.target.value, 10));
          }}
        />
      </Box>

      <Drawer
        anchor="right"
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        PaperProps={{ sx: { width: 420, backgroundColor: '#181818', p: 2 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="#f3f4f6" sx={{ flex: 1 }}>
            Edit Holiday
          </Typography>
          <IconButton onClick={() => setEditTarget(null)} sx={{ color: '#9ca3af' }}>
            <Close />
          </IconButton>
        </Box>
        {editTarget && (
          <AddHolidayForm
            holiday={editTarget}
            onSaved={() => {
              setEditTarget(null);
              fetchHolidays();
            }}
            handleDrawerClose={() => setEditTarget(null)}
          />
        )}
      </Drawer>

      <DialogBoxComp
        open={Boolean(deleteTarget)}
        title="Delete Holiday"
        content={`Delete holiday on ${deleteTarget ? dayjs(deleteTarget.date).format('DD MMM YYYY') : ''}? Attendance for that day will be re-evaluated by the policy engine.`}
        handleClose={() => setDeleteTarget(null)}
        handleConfirm={handleDelete}
      />
    </Box>
  );
};

export default withComponentAuth(HolidaysManagement);
